import { FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import classnames from 'classnames';

import Opinion, { Statuses } from 'models/Opinion';

import { list as listOpinions } from 'requests/opinions';

import DeviceContext from 'components/context/Device';
import LocaleContext from 'components/context/Locale';
import Button, { ButtonElements, ButtonLayouts } from 'components/layout/Button';
import LayoutContainer from 'components/layout/LayoutContainer';
import OpinionBox from 'components/modules/public/layout/boxes/OpinionBox';

import StyledComponent from './styles';
import { Props, Translations } from './types';

const Slick = dynamic(() => import('components/layout/Slick'));
const MobileSlider = dynamic(() => import('components/layout/MobileSlider'));

const PublicLayoutOpinions: FunctionComponent<Props> = ({ opinions, headline, perPage }) => {
    const [inactiveShowSliderInteraction, setInactiveShowSliderInteraction] = useState<boolean>(false);
    const [revealOpinions, setRevealOpinions] = useState<boolean>(false);
    const deviceContext = useContext(DeviceContext);
    const localeContext = useContext(LocaleContext);
    const [opinionsList, setOpinionsList] = useState<Opinion[]>(opinions);
    const containerRef = useRef<number>(null);
    const list = opinionsList || [];
    const tranlations: Translations = localeContext?.translations?.blockOpinions;

    const touchStart = (e) => containerRef.current = e.touches[0].clientX;

    const touchMove = (e) => {
        if (deviceContext?.isMobile) return;
        if (inactiveShowSliderInteraction) return;

        const clientX = e.touches[0].clientX;

        function getDifference(a: number, b: number) {
            return Math.abs(a - b);
        }

        const difference = getDifference(clientX, containerRef.current);

        if (difference > 80) return setInactiveShowSliderInteraction(true);
    };

    const settings = {
        arrows: false,
        dots: true,
        slidesToShow: list.length > 2 ? 3 : list.length,
        slidesToScroll: list.length > 2 ? 3 : 1,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 2,
                },
            },
        ],
    };

    useEffect(() => {
        if (!opinions) {
            listOpinions({
                page: 1,
                perPage: perPage,
                status: Statuses.Approved,
            })
                .then(response => setOpinionsList(response.elements
                    .map(element => new Opinion(element))
                ));
        }
    }, []);

    return (
        <StyledComponent className={classnames('public-layout-opinions', revealOpinions && 'list-expanded')}>
            <LayoutContainer>
                <div
                    className="headline"
                    dangerouslySetInnerHTML={{ __html: headline }}
                />
                {!deviceContext?.isMobile && !revealOpinions && (
                    <div
                        className="list"
                        onTouchStart={touchStart}
                        onTouchMove={touchMove}
                    >
                        {Array.isArray(list) && list.length > 0 && (
                            <Slick sliderProps={settings}>
                                {list.map(listElem => (
                                    <OpinionBox
                                        key={listElem.id}
                                        {...listElem}
                                    />
                                ))}
                            </Slick>
                        )}
                    </div>
                )}

                {!deviceContext?.isMobile && revealOpinions && (
                    <div className="list-expanded-container">
                        {Array.isArray(list) && list.length > 0 && (
                            <div className="expanded-opinions">
                                {list.map(listElem => (
                                    <OpinionBox
                                        key={listElem.id}
                                        {...listElem}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {deviceContext?.isMobile && (
                    <>
                        {Array.isArray(list) && list.length > 0 && (
                            <MobileSlider>
                                {list.map(listElem => (
                                    <OpinionBox
                                        key={listElem.id}
                                        {...listElem}
                                    />
                                ))}
                            </MobileSlider>
                        )}
                    </>
                )}

                {!deviceContext?.isMobile && !revealOpinions && (
                    <div className="show-more-opinions">
                        <Button
                            className="control"
                            layout={ButtonLayouts.Default}
                            element={ButtonElements.Button}
                            onClick={() => setRevealOpinions(!revealOpinions)}
                        >
                            {tranlations?.seeMore}
                        </Button>
                    </div>
                )}

                {!deviceContext?.isMobile && revealOpinions && (
                    <div className="show-less-opinions">
                        <Button
                            className="control"
                            layout={ButtonLayouts.Default}
                            element={ButtonElements.Button}
                            onClick={() => setRevealOpinions(!revealOpinions)}
                        >
                            {tranlations?.seeLess}
                        </Button>
                    </div>
                )}
            </LayoutContainer>
        </StyledComponent>
    );
};

export default PublicLayoutOpinions;
